import "twin.macro";

import { useQueryClient } from "@tanstack/react-query";

import { OpaqueCard } from "@components/StyledComponents";
import IndexTable, { PaginatedResourceProps } from "@features/ui/indexTable";
import SearchableText from "@features/ui/indexTable/SearchableText";
import { insertIf } from "@features/ui/indexTable/helpers";
import { Distributor, DistributorOrganization } from "@models/Distributor";
import useRoleIs from "@utils/useRoleIs";

import { distributorsKeyFactory } from "./data";

type DistributorTableProps = PaginatedResourceProps & {
  rows: Distributor[];
  query?: string;
  onRowClick?: (row: Distributor) => void;
};

const DistributorTable = ({ query, ...tableProps }: DistributorTableProps) => {
  const queryClient = useQueryClient();
  const roleIs = useRoleIs();

  return (
    <OpaqueCard tw="p-0">
      <IndexTable
        onRowClick={(row) =>
          queryClient.setQueryData<Distributor>(
            distributorsKeyFactory.detail(row.id).queryKey,
            row
          )
        }
        columns={[
          {
            id: "name",
            label: "Name",
            to: (row) => `/admin/distributors/${row.id}`,
            className: "font-medium underline",
            render: (name) => <SearchableText query={query} text={name} />,
          },
          ...insertIf(roleIs("super"), {
            id: "distributorOrganizations",
            label: "Organizations",
            render: (disOrgs: DistributorOrganization[]) =>
              disOrgs.map((org) => org.organization.name).join(", "),
          }),
          {
            id: "_userCount",
            label: "User Count",
            render: (_, dist) => dist.orderers.length,
          },
        ]}
        {...tableProps}
        noResultsText="No distributors found"
      />
    </OpaqueCard>
  );
};

export default DistributorTable;
